import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { catchError, switchMap, throwError, Observable, BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const platformId = inject(PLATFORM_ID);

  // Attach CSRF token for all requests in the browser if token exists
  if (isPlatformBrowser(platformId)) {
    req = addCsrfHeader(req);
  }

  return next(req).pipe(
    catchError((error) => {
      if (
        isPlatformBrowser(platformId) &&
        error instanceof HttpErrorResponse &&
        (error.status === 401 || error.status === 403) &&
        !req.url.includes('/api/auth/login') &&
        !req.url.includes('/api/auth/logout') &&
        !req.url.includes('/api/auth/refresh')
      ) {
        return handle403Error(req, next, authService);
      }
      return throwError(() => error);
    })
  );
};

function handle403Error(req: HttpRequest<any>, next: HttpHandlerFn, authService: AuthService): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((res: any) => {
        isRefreshing = false;

        if (res?.success !== false) {
          // Success: notify queued requests and retry the original one
          refreshTokenSubject.next(true);
          const retryReq = addCsrfHeader(req.clone({ withCredentials: true }));
          return next(retryReq);
        } else {
          // Failure (e.g. success: false but status 200 somehow)
          refreshTokenSubject.next(false);
          authService.deleteEverythingAndGoToLogin();
          return throwError(() => new Error('Session expired'));
        }
      }),
      catchError((err) => {
        // Refresh API returned an error (e.g. 401 Invalid Refresh Token)
        isRefreshing = false;
        refreshTokenSubject.next(false);
        authService.deleteEverythingAndGoToLogin();
        return throwError(() => err);
      })
    );
  } else {
    // Already refreshing — wait for the subject to emit a value other than null
    return refreshTokenSubject.pipe(
      filter(result => result !== null),
      take(1),
      switchMap(success => {
        if (success) {
          const retryReq = addCsrfHeader(req.clone({ withCredentials: true }));
          return next(retryReq);
        } else {
          // If false, the refresh failed. Don't retry, propagate the error.
          return throwError(() => new Error('Session expired'));
        }
      })
    );
  }
}

function addCsrfHeader(req: HttpRequest<any>): HttpRequest<any> {
  const csrfToken = getCookie('csrf-token');
  if (csrfToken) {
    return req.clone({
      setHeaders: { 'X-CSRF-Token': csrfToken },
    });
  }
  return req;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}
